import { useState, useCallback, useEffect, useRef } from "react";
import { useToast } from "@/lib/hooks/use-toast";
import { subscribeWebpush } from "@/server/requests/subscribe-webpush";
import { Session } from "next-auth";
import { env } from "@/env";

interface IBeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export type PwaReturn = {
  prompt: () => void;
  isIOS: boolean;
  isPWA: boolean;
  isStandalone: boolean;
  isInstallable: boolean;

  isSupported: boolean;
  isLoading: boolean;
  subscription: PushSubscription | null;
  subscribe: () => void;
  unsubscribe: () => void;

  isDrawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
};

export const pwaEmptyState = {
  prompt: () => undefined,
  isIOS: false,
  isPWA: false,
  isStandalone: false,
  isInstallable: false,

  isSupported: false,
  isLoading: true,
  subscription: null,
  subscribe: () => undefined,
  unsubscribe: () => undefined,

  isDrawerOpen: false,
  setDrawerOpen: () => undefined,
};

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function checkSupport() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    Notification?.permission !== "denied"
  );
}

export function useProgressiveWebApp(session: Session | null): PwaReturn {
  const { toast } = useToast();
  const promptRef = useRef<IBeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(pwaEmptyState.isIOS);
  const [isPWA, setIsPWA] = useState<boolean>(pwaEmptyState.isPWA);
  const [isStandalone, setIsStandalone] = useState(pwaEmptyState.isStandalone);
  const isInstallable = !isStandalone && (isIOS || isPWA);
  const [isSupported, setIsSupported] = useState(pwaEmptyState.isSupported);
  const [isLoading, setIsLoading] = useState(pwaEmptyState.isLoading);
  const [isDrawerOpen, setDrawerOpen] = useState(pwaEmptyState.isDrawerOpen);

  const prompt = () => {
    if (promptRef.current) {
      return promptRef.current.prompt();
    }
  };

  const [subscription, setSubscription] =
    useState<PwaReturn["subscription"]>(null);

  async function subscribe() {
    if (!isSupported) return;
    try {
      setIsLoading(true);
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          env.NEXT_PUBLIC_VAPID_PUBLIC_KEY
        ),
      });
      const response = await subscribeWebpush({
        action: "subscribe",
        subscription: sub.toJSON(),
      });
      if ("isError" in response) {
        sub.unsubscribe();
        toast(response);
      } else {
        setSubscription(sub);
      }
    } catch (e) {
      toast({
        variant: "destructive",
        title: "failed to enable notifications",
        description: (e as Error).message.toLowerCase(),
      });
      console.error(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }

  async function unsubscribe() {
    if (!subscription || !isSupported) return;
    try {
      setIsLoading(true);
      const response = await subscribeWebpush({
        action: "unsubscribe",
        subscription: subscription.toJSON(),
      });
      if ("isError" in response) {
        toast(response);
      } else {
        await subscription?.unsubscribe();
        setSubscription(null);
      }
    } catch (e) {
      toast({
        variant: "destructive",
        title: "failed to disable notifications",
        description: (e as Error).message.toLowerCase(),
      });
      console.error(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }

  const register = useCallback(async function () {
    try {
      setIsLoading(true);
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
        updateViaCache: "none",
      });
      const sub = await registration.pushManager.getSubscription();
      if (sub) {
        const response = await subscribeWebpush({
          action: "fetch",
          subscription: sub.toJSON(),
        });
        if ("isError" in response) {
          sub.unsubscribe();
        }
        setSubscription(sub);
      }
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!session) return;
    const ready = (e: IBeforeInstallPromptEvent) => {
      e.preventDefault();
      promptRef.current = e;
      setIsPWA(true);
    };

    const installed = () => {
      location.reload();
    };

    window.addEventListener("beforeinstallprompt", ready as any);
    window.addEventListener("appinstalled", installed);

    setIsIOS(
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
    );

    setIsStandalone(
      // display-mode works for chrome if not in fullscreen mode
      window.matchMedia("(display-mode: standalone)").matches ||
        // property only exists on safari
        "standalone" in navigator ||
        // fallback to be used when in chrome fullscreen mode is enabled
        !!sessionStorage.getItem("standalone")
    );

    setIsSupported(checkSupport());
    if (checkSupport()) {
      register();
    } else {
      setIsLoading(false);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", ready as any);
      window.removeEventListener("appinstalled", installed);
    };
  }, [session, register]);

  return {
    // pwa
    prompt,
    isIOS,
    isPWA,
    isStandalone,
    isInstallable,

    // sw
    isSupported,
    isLoading,
    subscription,
    subscribe,
    unsubscribe,

    // draw
    isDrawerOpen,
    setDrawerOpen,
  };
}
